import api from './api';

// 流程定义
export interface WorkflowDefinition {
  id: number;
  key: string;
  name: string;
  description: string;
  category: string;
  version: number;
  content: string;
  formConfig: string;
  status: number; // 0-草稿 1-已发布 2-已停用
  isLatest: boolean;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

// 流程实例
export interface WorkflowInstance {
  id: number;
  definitionId: number;
  definitionKey: string;
  definitionName: string;
  businessKey: string;
  businessType: string;
  title: string;
  initiatorId: string;
  initiatorName: string;
  currentNodeId: string;
  currentNodeName: string;
  status: number; // 1-运行中 2-已完成 3-已拒绝 4-已取消 5-已挂起 6-已终止
  variables: string;
  formData: string;
  startTime: string;
  endTime: string;
  duration: number;
  createdAt: string;
}

// 流程任务
export interface WorkflowTask {
  id: number;
  instanceId: number;
  nodeId: string;
  nodeName: string;
  nodeType: string;
  taskType: string;
  assigneeId: string;
  assigneeName: string;
  status: number; // 0-待处理 1-已通过 2-已拒绝 3-已转办 4-已委托 5-已撤回 6-已取消 7-已超时
  comment: string;
  formData: string;
  dueTime: string;
  completeTime: string;
  duration: number;
  createdAt: string;
  instance?: WorkflowInstance;
}

// 审批历史
export interface WorkflowHistory {
  id: number;
  instanceId: number;
  taskId: number;
  nodeId: string;
  nodeName: string;
  action: string;
  operatorId: string;
  operatorName: string;
  comment: string;
  duration: number;
  createdAt: string;
}

// 抄送记录
export interface WorkflowCC {
  id: number;
  instanceId: number;
  nodeId: string;
  userId: string;
  userName: string;
  isRead: boolean;
  readTime: string;
  createdAt: string;
}

// 委托配置
export interface WorkflowDelegate {
  id: number;
  userId: string;
  delegateToId: string;
  definitionKey: string;
  startTime: string;
  endTime: string;
  reason: string;
  status: number;
  createdAt: string;
}

// ========== 流程定义 API ==========

export const createDefinition = (data: Partial<WorkflowDefinition>) => {
  return api.post('/workflow/definitions', data);
};

export const updateDefinition = (id: number, data: Partial<WorkflowDefinition>) => {
  return api.put(`/workflow/definitions/${id}`, data);
};

export const publishDefinition = (id: number) => {
  return api.post(`/workflow/definitions/${id}/publish`);
};

export const disableDefinition = (id: number) => {
  return api.post(`/workflow/definitions/${id}/disable`);
};

export const enableDefinition = (id: number) => {
  return api.post(`/workflow/definitions/${id}/enable`);
};

export const deleteDefinition = (id: number) => {
  return api.delete(`/workflow/definitions/${id}`);
};

export const getDefinition = (id: number) => {
  return api.get<WorkflowDefinition>(`/workflow/definitions/${id}`);
};

export const listDefinitions = (params: {
  category?: string;
  status?: number;
  page?: number;
  pageSize?: number;
}) => {
  return api.get<{ list: WorkflowDefinition[]; total: number }>('/workflow/definitions', { params });
};

// ========== 流程实例 API ==========

export const startProcess = (data: {
  definitionKey: string;
  businessKey?: string;
  businessType?: string;
  title: string;
  variables?: Record<string, any>;
  formData?: Record<string, any>;
}) => {
  return api.post<WorkflowInstance>('/workflow/instances', data);
};

export const getInstance = (id: number) => {
  return api.get<WorkflowInstance>(`/workflow/instances/${id}`);
};

export const getInstanceHistory = (id: number) => {
  return api.get<WorkflowHistory[]>(`/workflow/instances/${id}/history`);
};

export const getInstanceTasks = (id: number) => {
  return api.get<WorkflowTask[]>(`/workflow/instances/${id}/tasks`);
};

export const withdrawProcess = (id: number, comment?: string) => {
  return api.post(`/workflow/instances/${id}/withdraw`, { comment });
};

export const cancelProcess = (id: number, comment?: string) => {
  return api.post(`/workflow/instances/${id}/cancel`, { comment });
};

// ========== 任务处理 API ==========

export const approveTask = (id: number, data: {
  action: 'approve' | 'reject';
  comment?: string;
  formData?: Record<string, any>;
}) => {
  return api.post(`/workflow/tasks/${id}/approve`, data);
};

export const transferTask = (id: number, data: {
  toUserId: string;
  toUserName: string;
  comment?: string;
}) => {
  return api.post(`/workflow/tasks/${id}/transfer`, data);
};

export const delegateTask = (id: number, data: {
  toUserId: string;
  toUserName: string;
  comment?: string;
}) => {
  return api.post(`/workflow/tasks/${id}/delegate`, data);
};

// ========== 我的工作台 API ==========

export const getMyTodoTasks = (params: { page?: number; pageSize?: number }) => {
  return api.get<{ list: WorkflowTask[]; total: number }>('/workflow/my/todo', { params });
};

export const getMyDoneTasks = (params: { page?: number; pageSize?: number }) => {
  return api.get<{ list: WorkflowTask[]; total: number }>('/workflow/my/done', { params });
};

export const getMyInitiated = (params: { status?: number; page?: number; pageSize?: number }) => {
  return api.get<{ list: WorkflowInstance[]; total: number }>('/workflow/my/initiated', { params });
};

export const getMyCCs = (params: { isRead?: boolean; page?: number; pageSize?: number }) => {
  return api.get<{ list: WorkflowCC[]; total: number }>('/workflow/my/cc', { params });
};

export const markCCRead = (id: number) => {
  return api.post(`/workflow/my/cc/${id}/read`);
};

// ========== 委托管理 API ==========

export const createDelegate = (data: Partial<WorkflowDelegate>) => {
  return api.post('/workflow/delegates', data);
};

export const getMyDelegates = () => {
  return api.get<WorkflowDelegate[]>('/workflow/delegates');
};

export const cancelDelegate = (id: number) => {
  return api.delete(`/workflow/delegates/${id}`);
};

// 状态映射
export const instanceStatusMap: Record<number, { text: string; color: string }> = {
  1: { text: '运行中', color: 'processing' },
  2: { text: '已完成', color: 'success' },
  3: { text: '已拒绝', color: 'error' },
  4: { text: '已取消', color: 'default' },
  5: { text: '已挂起', color: 'warning' },
  6: { text: '已终止', color: 'error' },
};

export const taskStatusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待处理', color: 'processing' },
  1: { text: '已通过', color: 'success' },
  2: { text: '已拒绝', color: 'error' },
  3: { text: '已转办', color: 'warning' },
  4: { text: '已委托', color: 'warning' },
  5: { text: '已撤回', color: 'default' },
  6: { text: '已取消', color: 'default' },
  7: { text: '已超时', color: 'error' },
};

export const definitionStatusMap: Record<number, { text: string; color: string }> = {
  0: { text: '草稿', color: 'default' },
  1: { text: '已发布', color: 'success' },
  2: { text: '已停用', color: 'error' },
};
